import { useMemo, useRef, useState } from 'react';
import {
  Activity as ActivityIcon,
  CalendarClock,
  CalendarDays,
  Check,
  CircleDashed,
  Image as ImageIcon,
  Pencil,
  Plus,
  SignalHigh,
  SlidersHorizontal,
  Tag,
  UserRound,
  Users,
} from 'lucide-react';

import { useUrlState } from '@/hooks/useUrlState';
import { Button } from '@/components/ui/button';
import { EntitySelect } from '@/components/EntitySelect';
import { PersonAvatar } from '@/components/PersonAvatar';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RichTextContent, RichTextEditor } from '@/components/ui/rich-text-editor';
import { useSession } from '@/stores/session';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { PRIORITIES, PROJECT_STATUSES, STATE_GROUPS } from '../constants';
import {
  projectLabelsHooks,
  projectMembersHooks,
  useProjectActivity,
  useProjectProgress,
  useUpdateProject,
  useUploadProjectCover,
} from '../queries';
import { resolveMemberByIdLabel, searchMembersById } from '../select';
import type {
  Priority,
  Project,
  ProjectActivity,
  ProjectLabel,
  ProjectMember,
  ProjectStatus,
} from '../types';

const GROUP_COLOR: Record<string, string> = {
  backlog: '#94a3b8',
  unstarted: '#64748b',
  started: '#f59e0b',
  completed: '#22c55e',
  cancelled: '#ef4444',
};

export function ProjectOverviewTab({
  project,
  showPanel,
}: {
  project: Project;
  showPanel: boolean;
}) {
  const update = useUpdateProject(project.public_id);
  const uploadCover = useUploadProjectCover(project.public_id);
  const coverInput = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(project.name);
  // View-first description: links stay clickable until an explicit Edit
  // (a live editor swallows link clicks — DEV13-121).
  const [editingDesc, setEditingDesc] = useState(false);
  // Editing the project (name/cover/description) + the side panel (properties /
  // activity / labels) are staff-only; guests get a read-only Overview
  // (progress + description).
  const isGuest = useSession((s) => s.role) === 'guest';

  return (
    <div>
      <div className="flex gap-6">
        <div className="min-w-0 flex-1">
          {/* Cover + overlapping icon */}
          <div className="relative">
            <div className="h-40 overflow-hidden rounded-lg bg-gradient-to-br from-slate-700 to-slate-900">
              {project.cover ? (
                <img src={project.cover} alt="" className="size-full object-cover" />
              ) : null}
            </div>
            <input
              ref={coverInput}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadCover.mutate(file);
                e.target.value = '';
              }}
            />
            {!isGuest ? (
              <Button
                variant="secondary"
                size="sm"
                className="absolute top-3 right-3"
                onClick={() => coverInput.current?.click()}
                disabled={uploadCover.isPending}
              >
                <ImageIcon className="size-4" /> Change cover
              </Button>
            ) : null}
            <div className="bg-background absolute -bottom-5 left-5 flex size-14 items-center justify-center rounded-lg border text-3xl shadow-sm">
              {project.icon || '📁'}
            </div>
          </div>

          {/* Name + description (cleared below the overlapping icon) */}
          <div className="mt-8">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => name.trim() && name !== project.name && update.mutate({ name: name.trim() })}
              disabled={isGuest}
              className="h-auto border-0 px-0 text-2xl font-semibold shadow-none focus-visible:ring-0 disabled:cursor-text disabled:opacity-100"
            />
            {/* Cap the description width instead of running full-page edge-to-edge
                (long-form prose reads poorly at full width). */}
            <div className="mt-1 flex max-w-5xl items-start gap-2">
              <div className="min-w-0 flex-1">
                {!isGuest && editingDesc ? (
                  <RichTextEditor
                    minimal
                    focusOnMount
                    placeholder="Add a description…"
                    value={project.description}
                    onChange={(html) => html !== project.description && update.mutate({ description: html })}
                  />
                ) : project.description ? (
                  <RichTextContent html={project.description} />
                ) : !isGuest ? (
                  <button
                    type="button"
                    onClick={() => setEditingDesc(true)}
                    className="text-muted-foreground hover:text-foreground py-1 text-sm"
                  >
                    Click to add description
                  </button>
                ) : null}
              </div>
              {!isGuest ? (
                <button
                  type="button"
                  aria-label={editingDesc ? 'Done editing description' : 'Edit description'}
                  title={editingDesc ? 'Done' : 'Edit'}
                  onClick={() => setEditingDesc((v) => !v)}
                  className="text-muted-foreground hover:bg-muted hover:text-foreground mt-0.5 rounded p-1"
                >
                  {editingDesc ? <Check className="size-4" /> : <Pencil className="size-3.5" />}
                </button>
              ) : null}
            </div>
          </div>

          <ProgressSection project={project} />
        </div>

        {showPanel && !isGuest ? <SidePanel project={project} update={update} /> : null}
      </div>
    </div>
  );
}

function ProgressSection({ project }: { project: Project }) {
  const { data: progress } = useProjectProgress(project.public_id);
  const total = progress?.total ?? 0;

  if (total === 0) {
    return (
      <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed py-10 text-center">
        <SignalHigh className="text-muted-foreground/40 size-10" />
        <p className="mt-3 font-medium">There&rsquo;s no progress to show yet.</p>
        <p className="text-muted-foreground max-w-sm text-sm">
          Set states on work items to see progress metrics here.
        </p>
      </div>
    );
  }

  return (
    <section className="mt-8 max-w-xl space-y-2">
      <h2 className="text-sm font-medium">Progress</h2>
      <div className="bg-muted flex h-2.5 overflow-hidden rounded-full">
        {STATE_GROUPS.map((g) => {
          const n = progress?.by_group[g.value] ?? 0;
          if (!n) return null;
          return (
            <div
              key={g.value}
              style={{ width: `${(n / total) * 100}%`, backgroundColor: GROUP_COLOR[g.value] }}
              title={`${g.label}: ${n}`}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
        {STATE_GROUPS.map((g) => (
          <span key={g.value} className="text-muted-foreground flex items-center gap-1.5 text-xs">
            <span className="size-2 rounded-full" style={{ backgroundColor: GROUP_COLOR[g.value] }} />
            {g.label} {progress?.by_group[g.value] ?? 0}
          </span>
        ))}
      </div>
    </section>
  );
}

function SidePanel({
  project,
  update,
}: {
  project: Project;
  update: ReturnType<typeof useUpdateProject>;
}) {
  const [tab, setTab] = useUrlState<'properties' | 'activity'>('overviewTab', 'properties');

  return (
    <aside className="w-72 shrink-0 border-l pl-5">
      <div className="mb-3 flex gap-1">
        <PanelTab icon={SlidersHorizontal} label="Properties" active={tab === 'properties'} onClick={() => setTab('properties')} />
        <PanelTab icon={ActivityIcon} label="Activity" active={tab === 'activity'} onClick={() => setTab('activity')} />
      </div>
      {tab === 'properties' ? (
        <PropertiesPanel project={project} update={update} />
      ) : (
        <ActivityPanel project={project} />
      )}
    </aside>
  );
}

function PanelTab({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: typeof SignalHigh;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm font-medium',
        active ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      <Icon className="size-4" />
      {label}
    </button>
  );
}

function PropertiesPanel({
  project,
  update,
}: {
  project: Project;
  update: ReturnType<typeof useUpdateProject>;
}) {
  const membersQuery = projectMembersHooks.useList({ project: project.id });
  const members = useMemo(() => {
    const raw = membersQuery.data;
    return (Array.isArray(raw) ? raw : (raw?.results ?? [])) as ProjectMember[];
  }, [membersQuery.data]);

  return (
    <div className="space-y-3 text-sm">
        <Row icon={CircleDashed} label="State">
          <Select value={project.status} onValueChange={(v) => update.mutate({ status: v as ProjectStatus })}>
            <SelectTrigger className="h-8 w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PROJECT_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Row>
        <Row icon={SignalHigh} label="Priority">
          <Select value={project.priority} onValueChange={(v) => update.mutate({ priority: v as Priority })}>
            <SelectTrigger className="h-8 w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Row>
        <Row icon={UserRound} label="Lead">
          <div className="w-40">
            <EntitySelect
              queryKey="project-lead-picker"
              value={project.lead ? String(project.lead) : ''}
              onChange={(v) => update.mutate({ lead: v ? Number(v) : null })}
              search={searchMembersById}
              resolveLabel={resolveMemberByIdLabel}
              placeholder="None"
              clearable
            />
          </div>
        </Row>
        <Row icon={Users} label="Members">
          <span className="text-muted-foreground w-40 truncate text-right">
            {members.length === 0
              ? 'Manage in Settings'
              : members.map((m) => m.member_name).filter(Boolean).join(', ')}
          </span>
        </Row>
        <Row icon={Tag} label="Labels">
          <ProjectLabelsField project={project} update={update} />
        </Row>
        <Row icon={CalendarDays} label="Start date">
          <Input
            type="date"
            defaultValue={project.start_date ?? ''}
            onBlur={(e) => e.target.value !== (project.start_date ?? '') && update.mutate({ start_date: e.target.value || null })}
            className="h-8 w-40"
          />
        </Row>
        <Row icon={CalendarClock} label="Due date">
          <Input
            type="date"
            defaultValue={project.end_date ?? ''}
            onBlur={(e) => e.target.value !== (project.end_date ?? '') && update.mutate({ end_date: e.target.value || null })}
            className="h-8 w-40"
          />
        </Row>
    </div>
  );
}

function ActivityPanel({ project }: { project: Project }) {
  // Activity targets the project by its numeric pk (record_activity stores
  // object_id = str(project.id)), not the public_id — filter must match.
  const { data, isLoading } = useProjectActivity(project.id);
  const items = data?.results ?? [];

  if (isLoading) {
    return <p className="text-muted-foreground text-sm">Loading activity…</p>;
  }
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <ActivityIcon className="text-muted-foreground/40 size-8" />
        <p className="text-muted-foreground mt-3 text-sm">No activity yet.</p>
      </div>
    );
  }

  return (
    <ol className="space-y-4">
      {items.map((a) => (
        <ActivityRow key={a.id} activity={a} />
      ))}
    </ol>
  );
}

function ActivityRow({ activity }: { activity: ProjectActivity }) {
  const to = typeof activity.data?.to === 'string' ? activity.data.to : null;
  return (
    <li className="relative flex gap-3 text-sm">
      <PersonAvatar
        name={activity.actor_name ?? 'Someone'}
        photo={activity.actor_photo}
        className="mt-0.5"
      />
      <div className="min-w-0">
        <p className="leading-snug">
          <span className="font-medium">{activity.actor_name ?? 'Someone'}</span>{' '}
          <span className="text-muted-foreground">{activity.verb}</span>
          {to ? <span className="font-medium"> {to}</span> : null}
        </p>
        <p className="text-muted-foreground text-xs">{timeAgo(activity.created)}</p>
      </div>
    </li>
  );
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const secs = Math.round((Date.now() - then) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function ProjectLabelsField({
  project,
  update,
}: {
  project: Project;
  update: ReturnType<typeof useUpdateProject>;
}) {
  const labelsQuery = projectLabelsHooks.useList();
  const createLabel = projectLabelsHooks.useCreate();
  const [newName, setNewName] = useState('');

  const all = useMemo(() => {
    const raw = labelsQuery.data;
    return (Array.isArray(raw) ? raw : (raw?.results ?? [])) as ProjectLabel[];
  }, [labelsQuery.data]);

  const assigned = new Set(project.labels);

  function toggle(id: number) {
    const next = assigned.has(id)
      ? project.labels.filter((x) => x !== id)
      : [...project.labels, id];
    update.mutate({ labels: next });
  }

  function createAndAssign() {
    if (!newName.trim()) return;
    createLabel.mutate(
      { name: newName.trim() },
      {
        onSuccess: (label) => {
          update.mutate({ labels: [...project.labels, label.id] });
          setNewName('');
        },
      },
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="hover:bg-muted/50 flex w-40 flex-wrap justify-end gap-1 rounded px-1 py-0.5">
          {project.label_details.length ? (
            project.label_details.map((l) => (
              <span
                key={l.id}
                className="flex items-center gap-1 rounded-full border px-1.5 text-xs"
              >
                <span className="size-2 rounded-full" style={{ backgroundColor: l.color || '#6366f1' }} />
                {l.name}
              </span>
            ))
          ) : (
            <span className="text-muted-foreground text-xs">Add labels</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-2">
        <div className="max-h-48 space-y-0.5 overflow-y-auto">
          {all.length === 0 ? (
            <p className="text-muted-foreground px-1 py-2 text-xs">No labels yet — create one below.</p>
          ) : (
            all.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => toggle(l.id)}
                className="hover:bg-muted flex w-full items-center gap-2 rounded px-2 py-1 text-sm"
              >
                <span className="size-2.5 rounded-full" style={{ backgroundColor: l.color || '#6366f1' }} />
                <span className="flex-1 truncate text-left">{l.name}</span>
                {assigned.has(l.id) ? <Check className="size-4" /> : null}
              </button>
            ))
          )}
        </div>
        <div className="mt-2 flex gap-1 border-t pt-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && createAndAssign()}
            placeholder="New label"
            className="h-7"
          />
          <Button
            size="icon"
            className="size-7 shrink-0"
            onClick={createAndAssign}
            disabled={!newName.trim() || createLabel.isPending}
            aria-label="Create label"
          >
            <Plus className="size-4" />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Row({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof SignalHigh;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('flex items-center justify-between gap-2')}>
      <span className="text-muted-foreground flex items-center gap-2">
        <Icon className="size-4" />
        {label}
      </span>
      {children}
    </div>
  );
}
